import { useState, useEffect } from 'react'
import { SimpleLoading, DialogAssign } from '../../pures'
import {
    Container,
    Grid,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Paper,
    Box,
    IconButton,
    Menu,
    MenuItem,
    Tooltip
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { TRAFFIC_LIGHT_COLOR, DEFAULT_TRAFFIC_LIGHT, getTrafficLightStatus, getTrafficLightTooltip } from '../../../utils'
import IFrame from '../../pures/dashboard/iframe'
import { Cancel } from '../../../assets/logos/icons'
import { ReactComponent as UserChatIcon } from '../../../assets/logos/user-chat.svg'
import '../search-field.css'

const containerImageCustomStyles = {
    margin: 'auto',
    width: '18px',
    height: '18px',
  }

const ChatsView = ({ chatbotUrl, isLoading = false, conversations = [], handleOnClickConversation, token, botId, sentimentEnabled = false, conversationIdParam, userId, setSelectedId, selectedId, assignButton, assignData, onAssignChat }) => {
    // #region variables
    const { t } = useTranslation('chats')
    const [query, setQuery] = useState('')
    const selectedConversation = (conversations.length > 0 && selectedId) && conversations.find(c => c.conversationId === selectedId) || null
    const iframeUrl = (selectedId && selectedConversation?.botConfigId && token)
      ? `${chatbotUrl}?botId=${selectedConversation.botConfigId}&oldConversationId=${selectedConversation.conversationId}&clientToken=${token}&userId=${userId}&viewerType=support_agent`
      : ''
    const [anchorEl, setAnchorEl] = useState(null)
    const [assignConversationId, setAssignConversationId] = useState(null)

    // modal dialog assign props
    const [showModalAssign, setShowModalAssign] = useState(false)
    // #endregion variables

    // #region effects
    useEffect(() => {
      setSelectedId(null)
    }, [botId])

    useEffect(() => setSelectedId(conversationIdParam.id), [conversationIdParam.counter])
    // #endregion effects

    // #region methods
    /**
     * @author Antonio Vazquez
     * @description Open option menu
     * @date 16-01-26
     * @param event default event value
     * @param conversationId conversation id
     * @return {void}
     */
    const handleMenuOpen = (event, conversationId) => {
      event.stopPropagation() // prevent conversation selection
      setAnchorEl(event.currentTarget)
      setAssignConversationId(conversationId)
    }

    /**
     * @author Antonio Vazquez
     * @description close option menu
     * @date 16-01-26
     * @return {void}
     */
    const handleMenuClose = () => {
      setAnchorEl(null)
    }

    /**
     * @author Antonio Vazquez
     * @description open modal to assign conversation/chat
     * @date 16-01-26
     * @return {void}
     */
    const handleOpenModalAssign = () => {
      setShowModalAssign(true)
      handleMenuClose()
    }

    /**
     * @author Antonio Vazquez
     * @description event to convert date [YYYY-MM-DD HH:mm:ss] to get only hour
     * @date 10-10-25
     * @param value date string with this format -> [YYYY-MM-DD HH:mm:ss]
     * @return {string}
     */
    const convertDateToHour = (value) => {
      let {hours, minutes, period} = ''
      try {
        if (value) {
          const arrayDate = value.split(' ')
          const arrayDateTime = arrayDate[1].split(':')
          hours = arrayDateTime[0]
          minutes = arrayDateTime[1]
          period = Number(hours) >= 12 ? 'PM' : 'AM'
        }
      } catch (error) {
        console.log(`An error ocurred in convertDateToHour: ${error}`)
      } finally {
        return (hours && minutes) ? `${hours}:${minutes} ${period}` : ''
      }
    }

    if (isLoading) {
        return (
            <div className='loading'>
                <SimpleLoading />
            </div>
        )
    }

    /**
     * @author Antonio Vazquez
     * @description close dialog assign
     * @date 20-01-26
     * @return {void}
     * @memberof ChatsView
     */
    const handleCloseDialogAssign = () => {
      setShowModalAssign(false)
    }
    // #endregion methods

    return (
      <Container maxWidth='lg' style={{ paddingTop: 16 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper
              style={{ height: '75vh', display: 'flex', overflow: 'hidden', boxShadow: '0px 4px 8px rgba(8, 8, 8, 0.15)' }}
            >
              {/* Sidebar: conversations */}
              <Box
                width='320px'
                borderRight='1px solid rgba(0,0,0,0.08)'
                display='flex'
                flexDirection='column'
              >
                <Box p={1} display='flex' alignItems='center'>
                  <TextField
                    className='project-search-field'
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('search') || 'Buscar o empezar nuevo chat'}
                    variant='outlined'
                    size='small'
                    fullWidth
                    InputProps={{
                      endAdornment: query && (
                        <IconButton
                          size='small'
                          onClick={() => setQuery('')}
                          style={{ padding: 4 }}
                        >
                          <img
                            src={Cancel}
                            alt=''
                            style={containerImageCustomStyles}
                          />
                        </IconButton>
                      ),
                    }}
                  />
                </Box>

                <Box flex='1' style={{ overflowY: 'auto' }}>
                  <List disablePadding>
                    {conversations
                      .filter((conv) =>
                        conv.conversationId?.toLowerCase().includes(query.toLowerCase())
                      )
                      .map((conv) => (
                        <ListItem
                          button
                          key={conv.conversationId}
                          selected={conv.conversationId === selectedId}
                          onClick={() => {
                            setSelectedId(conv.conversationId)
                            handleOnClickConversation(conv.conversationId)
                          }}
                        >
                          <ListItemAvatar>
                            {(() => {
                              const status = sentimentEnabled ? getTrafficLightStatus(conv.trafficLightStatus) : DEFAULT_TRAFFIC_LIGHT
                              const tooltip = sentimentEnabled ? getTrafficLightTooltip(t, conv.trafficLightStatus, conv.attentionStatus) : ''

                              const avatar = (
                                <div
                                  style={{
                                    position: 'relative',
                                    width: 40,
                                    height: 40,
                                    boxSizing: 'border-box',
                                    borderRadius: '50%',
                                    backgroundColor: '#f0f0f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                  className={`${!conv.finished && 'borderActiveChat'}`}
                                  aria-label={tooltip}
                                >
                                  <UserChatIcon
                                    style={{ width: 30, height: 30, color: TRAFFIC_LIGHT_COLOR[status] }}
                                  />
                                  {sentimentEnabled && conv.requiresReview && (
                                    <span className='reviewBadge' aria-hidden='true'>!</span>
                                  )}
                                  {conv.mode === 'realTime' && (
                                    <div className='iconChat'>
                                      <i className="k-icon k-icon-sm k-i-play"></i>
                                    </div>
                                  )}
                                </div>
                              )

                              return sentimentEnabled ? (
                                <Tooltip title={tooltip} arrow classes={{ tooltip: 'semaforoTooltip' }}>
                                  {avatar}
                                </Tooltip>
                              ) : avatar
                            })()}
                          </ListItemAvatar>
                          <ListItemText
                            className='chatText'
                            primary={
                              <span className='chatTitle'>
                                <span style={{ fontSize: '14px' }}>
                                  {conv.userData?.email ||
                                    conv.userData?.phone ||
                                    conv.userData?.ip && t('guest') ||
                                    t('guest')}
                                </span>
                                <span className='chatHour'>
                                  {convertDateToHour(conv?.updatedDate) || ''}
                                </span>
                              </span>
                            }
                            secondary={conv.conversationId && conv.conversationId.substring(conv.conversationId.length, conv.conversationId.length - 5) || ''}
                          />
                          {conv.mode === 'realTime' && conv.unseenMessages > 0 && (
                              <div className='notificationBadge'>
                                {conv.unseenMessages}
                              </div>
                          )}
                          {/* 3 point button only appear when assignButton = true */}
                          {assignButton && (
                            <IconButton
                              size='small'
                              onClick={(e) => handleMenuOpen(e, conv.conversationId)}
                              className='iconChatOptions'
                            >
                              <i className={'k-icon k-i-more-horizontal'}></i>
                            </IconButton>
                          )}
                        </ListItem>
                      ))}
                  </List>
                </Box>
              </Box>

              {/* dropdown menu */}
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={handleOpenModalAssign}>
                  {t('assign') || 'Asignar'}
                </MenuItem>
              </Menu>

              {/* Chat area */}
              {selectedId ? (
                <div className={'containerIframe'}>
                  <IFrame
                    key={selectedId}
                    height='100%'
                    width='100%'
                    url={iframeUrl}
                  />
                </div>
              ) : <div className={'containerNoIframe'}>
                  <p className='containerNoIframe-text'>{t('noSelectedChat')}</p>
                </div>}
            </Paper>
          </Grid>
        </Grid>
        {
          showModalAssign && <DialogAssign t={t} conversationId={assignConversationId} data={assignData} onClose={handleCloseDialogAssign} onAssign={onAssignChat} />
        }
      </Container>
    )
}

export default ChatsView